import { useEffect, useRef, useCallback, useState } from 'react';
import { Application, Container, Graphics } from 'pixi.js';
import { Viewport } from 'pixi-viewport';
import { useShopStore } from '../store/useShopStore';
import { getAsset } from '../ui/Palette';
import { 
  TILE_W, TILE_H,
  MIN_ZOOM, MAX_ZOOM, DEFAULT_ZOOM
} from '../config/constants';
import { isoToScreen, screenToIso, snapToGrid } from '../config/iso';
import { clearCache } from './SpriteCache';
import { drawIsoTile, createEntityContainer } from './EntityComponents';

export default function PixiStage({ bgColor = '#1a1a2e', gridColor = 0x3a3a3a, gridOpacity = 0.5 }) {
  const containerRef = useRef(null);
  const appRef = useRef(null);
  const viewportRef = useRef(null);
  const entityLayerRef = useRef(null);
  const overlayLayerRef = useRef(null);
  const floorLayerRef = useRef(null);
  
  // State to track when Pixi is initialized
  const [pixiReady, setPixiReady] = useState(false);
  
  // Ref to store the render entities function (created during init)
  const renderEntitiesRef = useRef(null);
  
  // Refs for avoiding stale closures in event handlers
  const currentToolRef = useRef('select');
  const selectedAssetKeyRef = useRef(null);
  const snapEnabledRef = useRef(true);
  
  const {
    layout,
    selectedId,
    hoveredId,
    currentTool,
    selectedAssetKey,
    snapEnabled,
    showGrid,
    setSelected,
    setHovered,
    setZoom,
    addFloorTile,
    removeFloorTile,
    addFloorTiles,
    removeFloorTiles,
    isFloorTile,
  } = useShopStore();
  
  // Get grid size from layout (default 30)
  const gridSize = layout.gridSize || 30;
  
  // Keep refs in sync with store values
  useEffect(() => {
    currentToolRef.current = currentTool;
    selectedAssetKeyRef.current = selectedAssetKey;
    snapEnabledRef.current = snapEnabled;
  }, [currentTool, selectedAssetKey, snapEnabled]);

  // Initialize Pixi Application
  useEffect(() => {
    if (!containerRef.current) return;

    let destroyed = false;
    let app = null;
    let canvas = null;

    const init = async () => {
      // Guard against null container
      if (!containerRef.current || destroyed) return;
      
      // Clear any existing canvases (Strict Mode safety)
      containerRef.current.innerHTML = '';

      // Use explicit size so Pixi doesn't trigger resize/render during init before we add the viewport.
      // resizeTo in init can cause "updateLocalTransform" null when the stage is still empty.
      const rect = containerRef.current.getBoundingClientRect();
      const initW = Math.max(rect.width || 800, 1);
      const initH = Math.max(rect.height || 600, 1);

      app = new Application();
      await app.init({
        width: initW,
        height: initH,
        backgroundAlpha: 0, // Transparent - let parent div control background
        antialias: true,
        resolution: window.devicePixelRatio || 1,
        autoDensity: true,
      });
      
      // If unmounted during async init, destroy immediately
      if (destroyed || !containerRef.current) {
        try { app.destroy(true); } catch (e) { /* ignore */ }
        return;
      }
      
      canvas = app.canvas;
      containerRef.current.appendChild(canvas);
      appRef.current = app;

      // Create viewport (stage now has valid size; we add viewport before any resize/render)
      const initialGridSize = useShopStore.getState().layout.gridSize || 30;
      const worldWidth = initialGridSize * TILE_W * 2; // Extra space for larger grids
      const worldHeight = initialGridSize * TILE_H * 2;
      
      const viewport = new Viewport({
        screenWidth: app.screen.width,
        screenHeight: app.screen.height,
        worldWidth,
        worldHeight,
        events: app.renderer.events,
      });

      viewport
        .drag({ mouseButtons: 'middle-right' })
        .pinch()
        .wheel()
        .decelerate()
        .clampZoom({ minScale: MIN_ZOOM, maxScale: MAX_ZOOM });

      viewport.moveCenter(0, initialGridSize * TILE_H / 4);
      viewport.setZoom(DEFAULT_ZOOM);

      viewport.on('zoomed', () => {
        setZoom(viewport.scale.x);
      });

      app.stage.addChild(viewport);
      viewportRef.current = viewport;

      // Create layers
      const floorLayer = new Container();
      floorLayer.label = 'floor';
      viewport.addChild(floorLayer);
      floorLayerRef.current = floorLayer;

      const entityLayer = new Container();
      entityLayer.label = 'entities';
      entityLayer.sortableChildren = true;
      viewport.addChild(entityLayer);
      entityLayerRef.current = entityLayer;

      const overlayLayer = new Container();
      overlayLayer.label = 'overlay';
      viewport.addChild(overlayLayer);
      overlayLayerRef.current = overlayLayer;

      // Draw grid on floor layer (initial) - uses initialGridSize from above
      drawGridOnLayer(floorLayer, gridColor, gridOpacity, initialGridSize);
      
      // Create interactive background for click detection
      const interactiveLayer = new Graphics();
      interactiveLayer.rect(-5000, -2000, 10000, 8000);
      interactiveLayer.fill({ color: 0x000000, alpha: 0.001 }); // Nearly invisible
      interactiveLayer.eventMode = 'static';
      interactiveLayer.cursor = 'crosshair';
      interactiveLayer.zIndex = -1000;
      floorLayer.addChild(interactiveLayer);
      
      // Create ghost preview for placement (lives in overlay layer)
      const ghostPreview = new Graphics();
      ghostPreview.visible = false;
      ghostPreview.alpha = 0.6;
      overlayLayer.addChild(ghostPreview);
      
      // Check if two tile footprints overlap
      const checkCollision = (tx1, ty1, w1, h1, tx2, ty2, w2, h2) => {
        // Check if rectangles in tile space overlap
        const noOverlap = 
          tx1 + w1 <= tx2 ||  // rect1 is left of rect2
          tx2 + w2 <= tx1 ||  // rect2 is left of rect1
          ty1 + h1 <= ty2 ||  // rect1 is above rect2
          ty2 + h2 <= ty1;    // rect2 is above rect1
        return !noOverlap;
      };
      
      // Check if placement would collide with any existing entity
      const wouldCollide = (tx, ty, footW, footH) => {
        const entities = useShopStore.getState().layout.entities;
        for (const entity of entities) {
          const asset = getAsset(entity.assetKey);
          const entityW = asset?.footprint?.w || 1;
          const entityH = asset?.footprint?.h || 1;
          
          if (checkCollision(tx, ty, footW, footH, entity.tx, entity.ty, entityW, entityH)) {
            return true;
          }
        }
        return false;
      };
      
      // Store current collision state for placement check
      let currentCollisionState = false;
      
      // Function to update ghost preview
      const updateGhostPreview = (worldX, worldY) => {
        const assetKey = selectedAssetKeyRef.current;
        const tool = currentToolRef.current;
        const snap = snapEnabledRef.current;
        
        if (tool !== 'place' || !assetKey) {
          ghostPreview.visible = false;
          currentCollisionState = false;
          return;
        }
        
        const { tx, ty } = screenToIso(worldX, worldY);
        const snapped = snap ? snapToGrid(tx, ty) : { tx, ty };
        const pos = isoToScreen(snapped.tx, snapped.ty);
        
        // Get asset info
        const asset = getAsset(assetKey);
        const footW = asset?.footprint?.w || 1;
        const footH = asset?.footprint?.h || 1;
        const baseColor = asset?.color || 0x6366f1;
        
        // Check for collision
        const hasCollision = wouldCollide(snapped.tx, snapped.ty, footW, footH);
        currentCollisionState = hasCollision;
        
        // Use red color if collision, otherwise normal color
        const fillColor = hasCollision ? 0xef4444 : baseColor;
        const strokeColor = hasCollision ? 0xfca5a5 : 0xffffff;
        
        // Redraw ghost as isometric tile at snapped position
        ghostPreview.clear();
        drawIsoTile(ghostPreview, footW, footH, fillColor, strokeColor, 0.5);
        ghostPreview.x = pos.x;
        ghostPreview.y = pos.y;
        ghostPreview.visible = true;
      };
      
      // Track mouse movement for ghost preview
      interactiveLayer.on('pointermove', (e) => {
        const worldPos = e.getLocalPosition(viewport);
        updateGhostPreview(worldPos.x, worldPos.y);
      });
      
      // Hide ghost when leaving the grid area
      interactiveLayer.on('pointerleave', () => {
        ghostPreview.visible = false;
      });
      
      // Map of entity ID -> { container, entity snapshot, selectedState, hoveredState }
      const entityContainerMap = new Map();
      let prevSelectedId = null;
      let prevHoveredId = null;
      
      // Drag state
      let dragState = {
        isDragging: false,
        entityId: null,
        startTx: 0,
        startTy: 0,
        currentTx: 0,
        currentTy: 0,
        hasCollision: false,
      };
      
      // Create drag ghost preview (separate from place ghost)
      const dragGhost = new Graphics();
      dragGhost.visible = false;
      dragGhost.alpha = 0.4;
      overlayLayer.addChild(dragGhost);
      
      // Drag handlers
      const handleDragStart = (entityId, event) => {
        const entity = useShopStore.getState().layout.entities.find(e => e.id === entityId);
        if (!entity) return;
        
        dragState = {
          isDragging: true,
          entityId,
          startTx: entity.tx,
          startTy: entity.ty,
          currentTx: entity.tx,
          currentTy: entity.ty,
          hasCollision: false,
        };
        
        // Show drag ghost
        const asset = getAsset(entity.assetKey);
        const footW = asset?.footprint?.w || 1;
        const footH = asset?.footprint?.h || 1;
        const baseColor = asset?.color || 0x6366f1;
        
        dragGhost.clear();
        drawIsoTile(dragGhost, footW, footH, baseColor, 0xffffff, 0.5);
        const pos = isoToScreen(entity.tx, entity.ty);
        dragGhost.x = pos.x;
        dragGhost.y = pos.y;
        dragGhost.visible = true;
      };
      
      const handleDragMove = (entityId, event) => {
        if (!dragState.isDragging || dragState.entityId !== entityId) return;
        
        const worldPos = event.getLocalPosition(viewport);
        const { tx, ty } = screenToIso(worldPos.x, worldPos.y);
        const snap = snapEnabledRef.current;
        const snapped = snap ? snapToGrid(tx, ty) : { tx: Math.round(tx), ty: Math.round(ty) };
        
        // Get entity for collision checking
        const entity = useShopStore.getState().layout.entities.find(e => e.id === entityId);
        if (!entity) return;
        
        const asset = getAsset(entity.assetKey);
        const footW = asset?.footprint?.w || 1;
        const footH = asset?.footprint?.h || 1;
        
        // Check collision (excluding self)
        const hasCollision = wouldCollideExcludingSelf(snapped.tx, snapped.ty, footW, footH, entityId);
        
        dragState.currentTx = snapped.tx;
        dragState.currentTy = snapped.ty;
        dragState.hasCollision = hasCollision;
        
        // Update ghost position and color
        const fillColor = hasCollision ? 0xef4444 : (asset?.color || 0x6366f1);
        const strokeColor = hasCollision ? 0xfca5a5 : 0xffffff;
        
        dragGhost.clear();
        drawIsoTile(dragGhost, footW, footH, fillColor, strokeColor, 0.5);
        const pos = isoToScreen(snapped.tx, snapped.ty);
        dragGhost.x = pos.x;
        dragGhost.y = pos.y;
      };
      
      const handleDragEnd = (entityId, event) => {
        if (!dragState.isDragging || dragState.entityId !== entityId) return;
        
        dragGhost.visible = false;
        
        // Only move if no collision and position actually changed
        if (!dragState.hasCollision && 
            (dragState.currentTx !== dragState.startTx || dragState.currentTy !== dragState.startTy)) {
          useShopStore.getState().updateEntityPos(entityId, dragState.currentTx, dragState.currentTy);
        }
        
        dragState = {
          isDragging: false,
          entityId: null,
          startTx: 0,
          startTy: 0,
          currentTx: 0,
          currentTy: 0,
          hasCollision: false,
        };
      };
      
      // Collision check excluding a specific entity (for drag)
      const wouldCollideExcludingSelf = (tx, ty, footW, footH, excludeId) => {
        const entities = useShopStore.getState().layout.entities;
        for (const entity of entities) {
          if (entity.id === excludeId) continue;
          const asset = getAsset(entity.assetKey);
          const entityW = asset?.footprint?.w || 1;
          const entityH = asset?.footprint?.h || 1;
          
          if (checkCollision(tx, ty, footW, footH, entity.tx, entity.ty, entityW, entityH)) {
            return true;
          }
        }
        return false;
      };
      
      // Create the render entities function - incremental with dirty flags
      renderEntitiesRef.current = (entities, selectedId, hoveredId) => {
        const layer = entityLayerRef.current;
        if (!layer) {
          console.error('entityLayerRef is null!');
          return;
        }
        
        // Track which entities currently exist
        const currentEntityIds = new Set(entities.map(e => e.id));
        
        // Remove containers for deleted entities
        for (const [id, data] of entityContainerMap.entries()) {
          if (!currentEntityIds.has(id)) {
            const cont = data.container;
            if (cont && !cont.destroyed && cont.parent === layer) {
              layer.removeChild(cont);
            }
            try {
              if (cont && !cont.destroyed) cont.destroy({ children: true });
            } catch (_) { /* ignore */ }
            entityContainerMap.delete(id);
          }
        }
        
        // Process each entity
        entities.forEach((entity) => {
          const { id, assetKey, _dirty } = entity;
          const asset = getAsset(assetKey);
          // Live status is keyed by MACHINE NAME (from the socket payload), not the entity uuid.
          // The binding lives on entity.meta.machineId (same key the Inspector reads).
          const boundMachine = entity.meta?.machineId;
          const machineStatus = (boundMachine ? useShopStore.getState().machineStatus[boundMachine] : null) || null;
          
          const isSelected = id === selectedId;
          const isHovered = id === hoveredId;
          
          const existing = entityContainerMap.get(id);
          
          // Check if we need to rebuild this entity
          const needsRebuild = !existing || _dirty || 
            existing.assetKey !== assetKey;
          
          // Check if selection state changed
          const selectionChanged = existing && (
            existing.isSelected !== isSelected || 
            existing.isHovered !== isHovered
          );
          
          if (needsRebuild) {
            // Remove old container if exists
            if (existing) {
              const oldCont = existing.container;
              if (oldCont && !oldCont.destroyed && oldCont.parent === layer) {
                layer.removeChild(oldCont);
              }
              try {
                if (oldCont && !oldCont.destroyed) oldCont.destroy({ children: true });
              } catch (_) { /* ignore */ }
            }

            // Create new entity container with drag support
            const entityContainer = createEntityContainer(entity, asset, {
              isSelected,
              isHovered,
              machineStatus,
              onClick: (entityId) => {
                if (currentToolRef.current === 'select') {
                  useShopStore.getState().setSelected(entityId);
                }
              },
              onDragStart: handleDragStart,
              onDragMove: handleDragMove,
              onDragEnd: handleDragEnd,
            });
            if (entityContainer && !entityContainer.destroyed) {
              layer.addChild(entityContainer);
              entityContainerMap.set(id, {
                container: entityContainer,
                assetKey,
                isSelected,
                isHovered,
                tx: entity.tx,
                ty: entity.ty,
              });
            }
            if (_dirty) {
              useShopStore.getState().clearEntityDirty(id);
            }
          } else if (selectionChanged) {
            // Just update selection visuals without full rebuild
            const oldCont = existing.container;
            if (oldCont && !oldCont.destroyed && oldCont.parent === layer) {
              layer.removeChild(oldCont);
            }
            try {
              if (oldCont && !oldCont.destroyed) oldCont.destroy({ children: true });
            } catch (_) { /* ignore */ }

            const entityContainer = createEntityContainer(entity, asset, {
              isSelected,
              isHovered,
              machineStatus,
              onClick: (entityId) => {
                if (currentToolRef.current === 'select') {
                  useShopStore.getState().setSelected(entityId);
                }
              },
              onDragStart: handleDragStart,
              onDragMove: handleDragMove,
              onDragEnd: handleDragEnd,
            });
            if (entityContainer && !entityContainer.destroyed) {
              layer.addChild(entityContainer);
              entityContainerMap.set(id, {
                container: entityContainer,
                assetKey,
                isSelected,
                isHovered,
                tx: entity.tx,
                ty: entity.ty,
              });
            }
          }
        });
        
        prevSelectedId = selectedId;
        prevHoveredId = hoveredId;
      };
      
      // Mark Pixi as ready - this will trigger entity rendering
      setPixiReady(true);
      console.log('Pixi initialized and ready');
      
      // Custom double-click detection (dblclick event unreliable in Pixi)
      let lastClickTime = 0;
      let lastClickPos = { x: 0, y: 0 };
      const DOUBLE_CLICK_THRESHOLD = 300; // ms
      const DOUBLE_CLICK_DISTANCE = 10; // pixels
      
      // Track floor drawing state
      let isFloorDrawing = false;
      let floorDrawMode = null; // 'draw' or 'erase'
      const drawnTiles = new Set();
      
      interactiveLayer.on('pointerdown', (e) => {
        const tool = currentToolRef.current;
        const assetKey = selectedAssetKeyRef.current;
        const snap = snapEnabledRef.current;
        const now = Date.now();
        const worldPos = e.getLocalPosition(viewport);
        
        // Handle floor draw/erase tools
        if (tool === 'floor-draw' || tool === 'floor-erase') {
          isFloorDrawing = true;
          floorDrawMode = tool === 'floor-draw' ? 'draw' : 'erase';
          drawnTiles.clear();
          
          const { tx, ty } = screenToIso(worldPos.x, worldPos.y);
          const gridSize = useShopStore.getState().layout.gridSize || 30;
          const snappedX = Math.floor(tx);
          const snappedY = Math.floor(ty);
          
          // Only process valid grid positions
          if (snappedX >= 0 && snappedX < gridSize && snappedY >= 0 && snappedY < gridSize) {
            const key = `${snappedX},${snappedY}`;
            if (!drawnTiles.has(key)) {
              drawnTiles.add(key);
              if (floorDrawMode === 'draw') {
                useShopStore.getState().addFloorTile(snappedX, snappedY);
              } else {
                useShopStore.getState().removeFloorTile(snappedX, snappedY);
              }
            }
          }
          return; // Don't process double-click for floor tools
        }
        
        // Check for double-click
        const timeDiff = now - lastClickTime;
        const distX = Math.abs(worldPos.x - lastClickPos.x);
        const distY = Math.abs(worldPos.y - lastClickPos.y);
        const isDoubleClick = timeDiff < DOUBLE_CLICK_THRESHOLD && 
                              distX < DOUBLE_CLICK_DISTANCE && 
                              distY < DOUBLE_CLICK_DISTANCE;
        
        if (isDoubleClick && tool === 'place' && assetKey) {
          // Place entity on double-click (only if no collision)
          if (!currentCollisionState) {
            const { tx, ty } = screenToIso(worldPos.x, worldPos.y);
            const snapped = snap ? snapToGrid(tx, ty) : { tx, ty };
            useShopStore.getState().addEntity(assetKey, snapped.tx, snapped.ty);
          }
          lastClickTime = 0; // Reset to prevent triple-click placing
        } else if (tool === 'select') {
          // Single click to deselect
          useShopStore.getState().setSelected(null);
        }
        
        lastClickTime = now;
        lastClickPos = { x: worldPos.x, y: worldPos.y };
      });
      
      // Floor drawing while dragging
      interactiveLayer.on('pointermove', (e) => {
        if (!isFloorDrawing) return;
        
        const worldPos = e.getLocalPosition(viewport);
        const { tx, ty } = screenToIso(worldPos.x, worldPos.y);
        const gridSize = useShopStore.getState().layout.gridSize || 30;
        const snappedX = Math.floor(tx);
        const snappedY = Math.floor(ty);
        
        // Only process valid grid positions
        if (snappedX >= 0 && snappedX < gridSize && snappedY >= 0 && snappedY < gridSize) {
          const key = `${snappedX},${snappedY}`;
          if (!drawnTiles.has(key)) {
            drawnTiles.add(key);
            if (floorDrawMode === 'draw') {
              useShopStore.getState().addFloorTile(snappedX, snappedY);
            } else {
              useShopStore.getState().removeFloorTile(snappedX, snappedY);
            }
          }
        }
      });
      
      // Stop floor drawing
      interactiveLayer.on('pointerup', () => {
        isFloorDrawing = false;
        drawnTiles.clear();
      });
      
      interactiveLayer.on('pointerupoutside', () => {
        isFloorDrawing = false;
        drawnTiles.clear();
      });
    };

    init();

    return () => {
      destroyed = true;
      setPixiReady(false);
      renderEntitiesRef.current = null;
      
      // Clear sprite cache on unmount
      clearCache();
      
      // Destroy the EXACT local app and remove EXACT local canvas
      try {
        if (app && typeof app.destroy === 'function') {
          app.destroy(true, { children: true });
        }
      } catch (e) {
        // Ignore errors during cleanup
      }
      
      try {
        if (canvas && canvas.parentNode) {
          canvas.parentNode.removeChild(canvas);
        }
      } catch (e) {
        // Ignore errors during cleanup
      }
      
      // Clear refs
      appRef.current = null;
      viewportRef.current = null;
      entityLayerRef.current = null;
      overlayLayerRef.current = null;
    };
  }, []);

  // Draw isometric grid on a layer
  const drawGridOnLayer = useCallback((layer, color, opacity, size) => {
    // Clear any existing grid graphics
    const existingGrid = layer.getChildByLabel?.('grid');
    if (existingGrid) {
      layer.removeChild(existingGrid);
      existingGrid.destroy();
    }
    
    const graphics = new Graphics();
    graphics.label = 'grid';
    
    // Draw grid lines - PixiJS v8 style
    for (let i = 0; i <= size; i++) {
      // Lines along X axis
      const startX = isoToScreen(i, 0);
      const endX = isoToScreen(i, size);
      graphics.moveTo(startX.x, startX.y);
      graphics.lineTo(endX.x, endX.y);
      
      // Lines along Y axis
      const startY = isoToScreen(0, i);
      const endY = isoToScreen(size, i);
      graphics.moveTo(startY.x, startY.y);
      graphics.lineTo(endY.x, endY.y);
    }
    
    // Apply stroke once for all lines
    graphics.stroke({ width: 1, color, alpha: opacity });
    
    layer.addChild(graphics);
  }, []);

  // Draw custom floor tiles
  const drawFloorTiles = useCallback((layer, floorTiles, size) => {
    // Clear any existing floor graphics
    const existingFloor = layer.getChildByLabel?.('floor-tiles');
    if (existingFloor) {
      layer.removeChild(existingFloor);
      existingFloor.destroy();
    }
    
    // If no custom floor, nothing to draw
    if (floorTiles === null) return;
    
    const graphics = new Graphics();
    graphics.label = 'floor-tiles';
    
    // Draw each floor tile with a subtle fill
    for (let x = 0; x < size; x++) {
      for (let y = 0; y < size; y++) {
        const isFloor = floorTiles.has(`${x},${y}`);
        if (isFloor) {
          const pos = isoToScreen(x, y);
          // Draw filled tile
          graphics.poly([
            pos.x, pos.y,                     // top
            pos.x + TILE_W/2, pos.y + TILE_H/2, // right
            pos.x, pos.y + TILE_H,            // bottom
            pos.x - TILE_W/2, pos.y + TILE_H/2, // left
          ]);
          graphics.fill({ color: 0x4a5568, alpha: 0.3 });
        }
      }
    }
    
    layer.addChildAt(graphics, 0); // Add behind grid
  }, []);

  // Update grid when color/opacity/size changes
  useEffect(() => {
    if (!floorLayerRef.current) return;
    drawGridOnLayer(floorLayerRef.current, gridColor, gridOpacity, gridSize);
    drawFloorTiles(floorLayerRef.current, layout.floorTiles, gridSize);
  }, [gridColor, gridOpacity, gridSize, layout.floorTiles, drawGridOnLayer, drawFloorTiles]);

  // Update entities when layout changes - call the render function created during init
  useEffect(() => {
    if (!pixiReady || !renderEntitiesRef.current) {
      console.log('Waiting for Pixi to be ready...');
      return;
    }
    
    // Call the render function that was created during Pixi init
    renderEntitiesRef.current(layout.entities, selectedId, hoveredId);
    
  }, [layout.entities, selectedId, hoveredId, pixiReady]);

  // Handle resize (we use explicit init size, so we must resize app + viewport when container size changes)
  useEffect(() => {
    const handleResize = () => {
      if (!containerRef.current || !appRef.current || !viewportRef.current) return;
      try {
        const app = appRef.current;
        const viewport = viewportRef.current;
        if (viewport.destroyed) return;
        const rect = containerRef.current.getBoundingClientRect();
        const w = Math.max(rect.width || 1, 1);
        const h = Math.max(rect.height || 1, 1);
        app.renderer.resize(w, h);
        viewport.resize(w, h);
      } catch (e) {
        console.warn('Pixi resize skipped:', e?.message);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      // Undo: Ctrl+Z / Cmd+Z
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        useShopStore.getState().undo();
        return;
      }
      
      // Redo: Ctrl+Y / Cmd+Y or Ctrl+Shift+Z / Cmd+Shift+Z
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        useShopStore.getState().redo();
        return;
      }
      
      switch (e.key) {
        case 'Delete':
        case 'Backspace':
          if (selectedId) {
            useShopStore.getState().deleteEntity(selectedId);
          }
          break;
        case 'Escape':
          setSelected(null);
          useShopStore.getState().setTool('select');
          useShopStore.getState().setSelectedAsset(null);
          break;
        case 'g':
          useShopStore.getState().toggleGrid();
          break;
        case 'r':
          // Rotate selected entity
          if (selectedId) {
            useShopStore.getState().rotateFacing(selectedId, e.shiftKey ? 'ccw' : 'cw');
          }
          break;
        case 's':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            useShopStore.getState().saveLayout();
          } else {
            useShopStore.getState().toggleSnap();
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedId, setSelected]);

  return (
    <div 
      ref={containerRef} 
      className="w-full h-full"
      style={{ touchAction: 'none' }}
      onContextMenu={(e) => e.preventDefault()}
    />
  );
}
